import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowLink } from '../components/RowLink';
import {
  Badge,
  Button,
  Card,
  Link,
  SearchBox,
  Spinner,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  shorthands,

  mergeClasses,
} from '@fluentui/react-components';
import { ChevronDownRegular, ChevronLeftRegular, ChevronRightRegular, ChevronUpRegular } from '@fluentui/react-icons';
import type { DeploymentSiteRollup } from '@tvmf/shared';
import { api } from '../api';
import { useAuth } from '../auth';
import { DataTable } from '../components/DataTable';
import { Page } from '../components/Page';
import { LoadError, NoTenant, useRecordStyles } from '../components/records';
import { TableSkeleton } from '../components/states';

const useLocalStyles = makeStyles({
  justifySpacebetween: { justifyContent: "space-between" },
  mb4: { marginBottom: '4px' },
  justifyFlexstartMt8Pl0: { marginTop: '8px', paddingLeft: '0px', justifyContent: "flex-start" },
  maxW320: { maxWidth: '320px' },
});


const useStyles = makeStyles({
  row: { display: 'flex', ...shorthands.gap('8px'), flexWrap: 'wrap', alignItems: 'center' },
  mono: { fontFamily: 'ui-monospace, monospace' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', ...shorthands.gap('8px') },
});

const CONNECTIONS_PAGE_SIZE = 5;

const DEPLOY_COLOR: Record<string, 'informative' | 'warning' | 'success' | 'danger'> = {
  queued: 'informative',
  running: 'informative',
  completed: 'success',
  failed: 'danger',
  cancelled: 'warning',
};

interface TenantRow {
  id: string;
  teams_read_only: boolean;
}
interface Connection {
  id: string;
  status: string;
  user_code: string | null;
  verification_uri: string | null;
  upn: string | null;
  started_at: string;
}

export function DeploymentSites() {
  const ls = useLocalStyles();
  const cs = useStyles();
  const s = useRecordStyles();
  const { activeTenantId, can } = useAuth();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['deployment-summary', activeTenantId],
    enabled: !!activeTenantId,
    queryFn: () => api<DeploymentSiteRollup[]>(`/t/${activeTenantId}/deployments/summary`),
  });

  const tenants = useQuery({ queryKey: ['tenants'], queryFn: () => api<TenantRow[]>('/tenants') });
  const teamsReadOnly = tenants.data?.find((t) => t.id === activeTenantId)?.teams_read_only ?? false;

  const connections = useQuery({
    queryKey: ['connections', activeTenantId],
    enabled: !!activeTenantId,
    refetchInterval: 4000,
    queryFn: () => api<Connection[]>(`/t/${activeTenantId}/deployments/connections`),
  });
  const connect = useMutation({
    mutationFn: () =>
      api(`/t/${activeTenantId}/deployments/connections`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['connections', activeTenantId] }),
  });
  const activeConn = connections.data?.find((c) => c.status === 'active');

  // Connections already come back newest-first, capped at 50 server-side
  // (DeploymentService.listConnections) - this just limits the table to 5
  // rows at a time instead of dumping the whole history on screen.
  const [connPage, setConnPage] = useState(1);
  const connTotal = connections.data?.length ?? 0;
  const connPages = Math.max(1, Math.ceil(connTotal / CONNECTIONS_PAGE_SIZE));
  const connPageClamped = Math.min(connPage, connPages);
  const pagedConnections = (connections.data ?? []).slice(
    (connPageClamped - 1) * CONNECTIONS_PAGE_SIZE,
    connPageClamped * CONNECTIONS_PAGE_SIZE,
  );
  const [search, setSearch] = useState('');
  // Collapsed by default - session history is rarely what a visit to this
  // page is for, and used to sit above the Sites list (the thing almost
  // every visit *is* for) as a 5-row table with its own pager. See
  // `latestConn` below for the compact status line shown instead.
  const [historyOpen, setHistoryOpen] = useState(false);
  const latestConn = connections.data?.[0];

  if (!activeTenantId) return <NoTenant />;
  if (q.isLoading) return <Spinner label="Loading Deployment…" />;
  if (q.isError) return <LoadError message={(q.error as Error).message} />;

  const term = search.trim().toLowerCase();
  const allSites = q.data ?? [];
  const sites = term
    ? allSites.filter((r) => r.sitecode.toLowerCase().includes(term) || (r.name ?? '').toLowerCase().includes(term))
    : allSites;

  return (
    <Page
      title="Deployment"
      subtitle="Sign in to the customer tenant live, then pick a site to review its planned changes, run them, and generate a change-recording document. No customer credentials are stored."
    >
      <Card className={s.card}>
        <div className={mergeClasses(s.cardHead, ls.justifySpacebetween)}>
          <Text weight="semibold" size={400}>
            Tenant connection
          </Text>
          {teamsReadOnly && (
            <Badge appearance="tint" color="warning" title="Live changes are disabled for this customer - every run is forced to dry-run.">
              Read-only tenant
            </Badge>
          )}
        </div>
        {connections.isLoading ? (
          <TableSkeleton rows={4} cols={5} />
        ) : (
          <>
            <div className={mergeClasses(cs.row, ls.mb4)}>
              {activeConn ? (
                <Badge appearance="tint" color="success">
                  Connected as {activeConn.upn ?? 'unknown'}
                </Badge>
              ) : latestConn?.status === 'pending' ? (
                <Badge appearance="tint" color="informative">
                  Sign-in pending
                </Badge>
              ) : (
                <Badge appearance="tint" color="subtle">
                  Not connected
                </Badge>
              )}
              {latestConn?.status === 'pending' && latestConn.user_code && (
                <Text size={200}>
                  <span className={cs.mono}>{latestConn.user_code}</span>{' '}
                  {latestConn.verification_uri && (
                    <Link href={latestConn.verification_uri} target="_blank" rel="noreferrer">
                      open sign-in
                    </Link>
                  )}
                </Text>
              )}
              {can('deployment:connect') && (
                <Button size="small" appearance="primary" onClick={() => connect.mutate()} disabled={connect.isPending}>
                  Connect to customer tenant
                </Button>
              )}
            </div>
            <Text size={200} className={s.muted}>
              {activeConn
                ? 'One connection covers every site below.'
                : 'Starts a device-code sign-in you complete in your browser. Planned changes can be previewed without a connection - only What-If/Execute runs need one.'}
            </Text>

            {connections.data && connections.data.length > 0 && (
              <>
                <Button
                  appearance="transparent"
                  size="small"
                  icon={historyOpen ? <ChevronUpRegular /> : <ChevronDownRegular />}
                  iconPosition="after"
                  onClick={() => setHistoryOpen((o) => !o)}
                  className={ls.justifyFlexstartMt8Pl0}
                >
                  {historyOpen ? 'Hide' : 'View'} connection history ({connTotal})
                </Button>
                {historyOpen && (
                  <>
                    <DataTable size="small" minWidth={720}>
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Started</TableHeaderCell>
                          <TableHeaderCell>Status</TableHeaderCell>
                          <TableHeaderCell>Device code</TableHeaderCell>
                          <TableHeaderCell>Signed in as</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedConnections.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell>{new Date(c.started_at).toLocaleString()}</TableCell>
                            <TableCell>
                              <Badge appearance="tint" color={c.status === 'active' ? 'success' : 'informative'}>
                                {c.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {c.status === 'pending' && c.user_code ? (
                                <span>
                                  <span className={cs.mono}>{c.user_code}</span>{' '}
                                  {c.verification_uri && (
                                    <Link href={c.verification_uri} target="_blank" rel="noreferrer">
                                      open sign-in
                                    </Link>
                                  )}
                                </span>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>{c.upn ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </DataTable>
                    {connPages > 1 && (
                      <div className={cs.pager}>
                        <Text size={200} className={s.muted}>
                          Page {connPageClamped} of {connPages} · {connTotal} total
                        </Text>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<ChevronLeftRegular />}
                          disabled={connPageClamped <= 1}
                          onClick={() => setConnPage((p) => Math.max(1, p - 1))}
                        />
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<ChevronRightRegular />}
                          disabled={connPageClamped >= connPages}
                          onClick={() => setConnPage((p) => Math.min(connPages, p + 1))}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </Card>

      <Card className={s.card}>
        <div className={s.cardHead}>
          <Text weight="semibold" size={400}>
            Sites{' '}
            <span className={s.muted}>
              ({sites.length}
              {term ? ` of ${allSites.length}` : ''})
            </span>
          </Text>
        </div>

        <SearchBox
          size="small"
          placeholder="Search by sitecode or name…"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          className={ls.maxW320}
        />

        {allSites.length === 0 ? (
          <Text size={200} className={s.muted}>
            No sites yet — add one in Site Data first.
          </Text>
        ) : sites.length === 0 ? (
          <Text size={200} className={s.muted}>
            No sites match “{search}”.
          </Text>
        ) : (
          <DataTable size="small" minWidth={780}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Sitecode</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Users</TableHeaderCell>
                <TableHeaderCell>CAPs</TableHeaderCell>
                <TableHeaderCell>Res. accts</TableHeaderCell>
                <TableHeaderCell>Last deployment</TableHeaderCell>
                <TableHeaderCell>Drift</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <RowLink to={`/deployment/sites/${r.id}`}>{r.sitecode}</RowLink>
                  </TableCell>
                  <TableCell>{r.name || '—'}</TableCell>
                  <TableCell>{r.counts.users}</TableCell>
                  <TableCell>{r.counts.caps}</TableCell>
                  <TableCell>{r.counts.resourceAccounts}</TableCell>
                  <TableCell>
                    {r.lastDeployment ? (
                      <Badge
                        appearance="tint"
                        color={
                          r.lastDeployment.status === 'completed' && r.lastDeployment.failed > 0
                            ? 'danger'
                            : DEPLOY_COLOR[r.lastDeployment.status] ?? 'informative'
                        }
                      >
                        {r.lastDeployment.mode === 'dry_run' ? 'dry run' : 'deployed'} ·{' '}
                        {r.lastDeployment.status === 'completed' && r.lastDeployment.failed > 0
                          ? `completed - ${r.lastDeployment.failed} failed`
                          : r.lastDeployment.status}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {r.drift && r.drift.drifted > 0 ? (
                      <RowLink to={`/deployment/sites/${r.id}?tab=drift`}>
                        <Badge appearance="tint" color="warning" title="Deployed rows that were changed in Teams afterwards, found by the last Discovery sync">
                          {r.drift.drifted} changed in Teams
                        </Badge>
                      </RowLink>
                    ) : r.drift ? (
                      <Text size={200} className={s.muted}>None</Text>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </Card>
    </Page>
  );
}
